import { Link } from 'react-router-dom';

const MaintenancePlans = () => {
  const features = [
    { icon: 'ri-alarm-warning-line', title: '24/7 Emergency Callout', desc: 'Round-the-clock emergency response whenever you need us, day or night.' },
    { icon: 'ri-refresh-line', title: 'Unlimited Reasonable Callouts', desc: 'Unlimited callouts subject to our fair usage policy — we come out as many times as genuinely needed.' },
    { icon: 'ri-camera-lens-line', title: 'Bi-Annual CCTV Survey', desc: 'Twice-yearly CCTV drain inspections to catch problems before they escalate.' },
    { icon: 'ri-flashlight-line', title: 'Same-Day Response', desc: 'Priority same-day attendance for all Premium Cover members.' },
    { icon: 'ri-water-flash-line', title: 'Free Drain Jetting', desc: 'High-pressure drain jetting included at no extra cost to keep drains clear.' },
    { icon: 'ri-price-tag-3-line', title: 'No Callout Charges', desc: 'Zero callout fees — ever. Your monthly plan covers every visit in full.' },
    { icon: 'ri-tools-line', title: 'Parts & Labour Included', desc: 'All parts and labour costs are covered under your Premium plan, up to claim limits.' },
    { icon: 'ri-user-star-line', title: 'Dedicated Account Manager', desc: 'A named account manager who knows your property and drainage history.' },
  ];

  return (
    <section id="plans" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900">
            Drainage Maintenance Plans
          </h2>
        </div>

        {/* Premium Cover Banner */}
        <div className="bg-gray-900 rounded-2xl px-8 py-10 mb-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-500/20 flex-shrink-0">
              <i className="ri-vip-crown-line text-3xl text-orange-500"></i>
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-white">Premium Cover</h3>
              <p className="text-gray-400 text-sm mt-1 max-w-xl">
                Our most comprehensive drainage protection plan — everything included, no hidden fees, no surprises. Repairs covered up to £2,000 per claim, £10,000 annually. Subject to inspection approval and fair usage policy.
              </p>
            </div>
          </div>
          <button
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-3 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            View Pricing
          </button>
        </div>

        {/* Coverage Limits Notice */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { icon: 'ri-money-pound-circle-line', label: 'Per Claim Limit', value: 'Up to £2,000', sub: 'Per individual repair claim' },
            { icon: 'ri-calendar-check-line', label: 'Annual Limit', value: 'Up to £10,000', sub: 'Maximum per property per year' },
            { icon: 'ri-time-line', label: 'Waiting Period', value: '30 Days', sub: 'Before non-emergency claims accepted' },
          ].map((item) => (
            <div key={item.label} className="bg-white border border-gray-100 rounded-2xl p-5 flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
                <i className={`${item.icon} text-orange-500 text-lg`}></i>
              </div>
              <div>
                <div className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">{item.label}</div>
                <div className="text-gray-900 font-extrabold text-lg leading-none">{item.value}</div>
                <div className="text-gray-400 text-xs mt-1">{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-6 border border-gray-100 hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 mb-4">
                <i className={`${f.icon} text-xl text-orange-500`}></i>
              </div>
              <h4 className="text-gray-900 font-bold text-sm mb-2">{f.title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Exclusions Summary */}
        <div className="mt-12 bg-white border border-gray-100 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
              <i className="ri-information-line text-orange-500 text-base"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-base">Key Exclusions — What Is Not Covered</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              'Pre-existing damage or defects',
              'Tree root ingress',
              'Subsidence or ground movement',
              'Shared or public drains',
              'Structural building works',
              'Cosmetic reinstatement',
              'Flooding or weather damage',
              'Neglect or lack of maintenance',
              'Works exceeding claim limits',
            ].map((excl) => (
              <div key={excl} className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-circle-line text-red-400 text-sm"></i>
                </div>
                {excl}
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-gray-50">
            <Link
              to="/terms"
              className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 text-sm font-semibold transition-colors cursor-pointer"
            >
              View full Terms &amp; Conditions
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>

        {/* Trust bar */}
        <div className="mt-10 flex flex-wrap justify-center gap-8 items-center">
          {[
            { icon: 'ri-award-line', text: 'Checkatrade Approved' },
            { icon: 'ri-shield-check-line', text: 'Public Liability Insured' },
            { icon: 'ri-map-pin-line', text: 'UK Mainland Only' },
            { icon: 'ri-star-line', text: '4.9★ Average Rating' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-gray-500 text-sm font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-orange-500`}></i>
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MaintenancePlans;
